// Remembers what went wrong on this page, so a bug report can carry it, and
// tells SSG about crashes nobody reports. Tiny and dependency-free: it is in
// the link pages too, which have a 120 KB budget.
export interface ConsoleEntry {
	at: string;
	level: "error" | "warn" | "request";
	message: string;
	stack?: string;
}

const MAX_ENTRIES = 50;
const MAX_REPORTS = 20;
const entries: ConsoleEntry[] = [];
const queue: { source: "web" | "link"; message: string; stack?: string; page: string }[] = [];
let sent = 0;
let timer: ReturnType<typeof setTimeout> | undefined;
let source: "web" | "link" | null = null;

/** Link tokens are secrets; emails are personal. Neither leaves the page. */
export function scrub(text: string): string {
	return text
		.replace(/\/(t|a|d|r|f|o|m|s|v|p|join)\/[^/?#\s"')]+/g, "/$1/•••")
		.replace(/[\w.+-]+@[\w-]+(\.[\w-]+)+/g, "[email]");
}

export const currentPage = () => scrub(location.pathname + location.search);

function text(value: unknown): string {
	if (value instanceof Error) return `${value.name}: ${value.message}`;
	if (typeof value === "string") return value;
	try {
		return JSON.stringify(value)?.slice(0, 500) ?? String(value);
	} catch {
		return String(value);
	}
}

export function note(level: ConsoleEntry["level"], message: string, stack?: string) {
	entries.push({
		at: new Date().toISOString(),
		level,
		message: scrub(message).slice(0, 2000),
		...(stack ? { stack: scrub(stack).slice(0, 4000) } : {}),
	});
	if (entries.length > MAX_ENTRIES) entries.shift();
}

/** What went wrong on this page, oldest first. */
export const recentEntries = (): ConsoleEntry[] => entries.slice();

// Noise that isn't ours to fix: browser extensions, cross-origin scripts, a benign layout warning.
const IGNORE = /ResizeObserver loop|^Script error\.?$|extension:\/\//;

function flush() {
	clearTimeout(timer);
	if (!queue.length) return;
	const problems = queue.splice(0, 5);
	fetch("/api/problems", {
		method: "POST",
		credentials: "same-origin",
		keepalive: true,
		headers: { "content-type": "application/json" },
		body: JSON.stringify({ problems }),
	}).catch(() => undefined);
	if (queue.length) timer = setTimeout(flush, 2000);
}

/** Tells SSG about a crash. Called by the global handlers and by the crash screen. */
export function reportProblem(error: unknown) {
	if (!source) return;
	const message = text(error).slice(0, 2000);
	const stack = error instanceof Error ? error.stack?.slice(0, 4000) : undefined;
	if (IGNORE.test(message) || (stack && IGNORE.test(stack)) || sent >= MAX_REPORTS) return;
	// The same crash twice in a row is one report.
	const last = queue.at(-1);
	if (last && last.message === scrub(message)) return;
	sent++;
	queue.push({ source, message: scrub(message), ...(stack ? { stack: scrub(stack) } : {}), page: currentPage() });
	clearTimeout(timer);
	timer = setTimeout(flush, 2000);
}

export function installCapture(from: "web" | "link") {
	if (source) return;
	source = from;
	for (const level of ["error", "warn"] as const) {
		const original = console[level];
		console[level] = (...args: unknown[]) => {
			const err = args.find((a) => a instanceof Error) as Error | undefined;
			note(level, args.map(text).join(" "), err?.stack);
			original.apply(console, args);
		};
	}
	addEventListener("error", (e) => {
		const err = e.error ?? e.message;
		note("error", text(err), e.error?.stack);
		reportProblem(err);
	});
	addEventListener("unhandledrejection", (e) => {
		note("error", `Unhandled: ${text(e.reason)}`, e.reason?.stack);
		reportProblem(e.reason);
	});
	addEventListener("pagehide", flush);
}
